import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import { Autocomplete, Box, Button, FormControl, Grid, Tab, TextField, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useTab } from 'src/components/hooks/useTab';
import {
  useGetAllPatientDetailsQuery,
  useLazyGetStudyAllCRFVisitsQuery,
  useLazyGetVisitCRFOIDsQuery
} from 'src/services/SubjectTrackingDetails';
import { setSelectedCRFOID } from 'src/store/CrfFormsAction';
import { hideLoader, showLoader } from 'src/store/Loader';
import { setMessage } from 'src/store/Message';
import { clearSubjectInfo, setSelectedSubject } from 'src/store/SelectedSubject';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import { usePreferedDateFormat } from 'src/util/DateFormatter';
import '../SubjectTracking.scss';
import { useSubjectTracking } from '../useSubjectTracking';
import CRFSTab from './CRFSTab';
import moment from 'moment';

const SubjectTrackingDetails: React.FunctionComponent<any> = ({
  prescreen_id,
  selectedSubjectID,
  setSelectedSubjectID,
  selectedVisitType,
  allSubjectsIDsList,
  setShowSubjectTraking,
  setShowSubjectDetails,
  handleSubjectIDChangeCallBack
}) => {
  const dispatch: any = useAppDispatch();
  const isLoaderInitiated = useAppSelector(state => state.loader.isProgress);
  const selectedSubjectInfo = useAppSelector(state => state.selectedSubjectInfo);
  const selectedCRF = selectedSubjectInfo.selected_crf;
  const preScreenGridRowsData: any = useAppSelector(state => state.preScreenGridRowsData.preScreenGridRowsData);
  const { preferedDateFormat, userPreferedDateFormat } = usePreferedDateFormat();
  const [summaryData, setSummaryData] = useState<any>({});

  const [value, setValue] = React.useState('1');
  const selectedVisitInDataReview = selectedSubjectInfo.visit_type;
  const selectedVisitIdFromCRFQC = selectedSubjectInfo.visit_id;
  const [selectedVisit, setSelectedVisit] = useState<any>(
    selectedVisitInDataReview !== ''
      ? { key: selectedVisitInDataReview, label: selectedVisitInDataReview, id: selectedVisitIdFromCRFQC }
      : { key: 'All', label: 'All' }
  );
  const handleChangeTabs = (_: React.SyntheticEvent, newValue: string) => {
    setValue(newValue);
    setSelectedVisit({ key: 'All', label: 'All' });
    if (newValue == '1' || newValue == '2') {
      setVisitList([{ key: 'All', label: 'All' }]);
    }
  };
  const [updateCRFGridData, setUpdateCRFGridData] = useState(false);
  const studyId: any = useAppSelector(state => state.site.selectedStudy);
  const selectedCountry = useAppSelector(state => state.site.selectedCountry);
  const selectedSite = useAppSelector(state => state.site.selectedSite);
  const [visitList, setVisitList] = useState<any>(['All']);
  const [patientID, setPatientID] = useState<any>(selectedSubjectInfo.patient_identifier);

  const [getVisitsAndCRFOIDs] = useLazyGetVisitCRFOIDsQuery();

  useEffect(() => {
    getVisitsAndCRFOIDs({
      site_id: selectedSite,
      study_id: studyId,
      country: selectedCountry
    });
  }, []);

  const {
    data: getAllPatientDetails,
    error: getAllPatientDetailsError,
    isError: getAllPatientDetailsIsError,
    isSuccess: getAllPatientDetailsIsSuccess,
    isLoading: getAllPatientDetailsIsLoading
  } = useGetAllPatientDetailsQuery({
    study_id: studyId,
    site_id: selectedSite,
    country: selectedCountry
  });

  const [
    getAllCRFVisits,
    {
      data: getAllCRFVisitsData,
      isError: getAllCRFVisitsIsError,
      isSuccess: getAllCRFVisitsIsSuccess,
      isLoading: getAllCRFVisitsIsLoading,
      error: getAllCRFVisitsError
    }
  ] = useLazyGetStudyAllCRFVisitsQuery();

  useEffect(() => {
    if (patientID) {
      getAllCRFVisits({ study_id: studyId, site_id: selectedSite, country: selectedCountry, patient_id: patientID });
    }
  }, [patientID]);

  useEffect(() => {
    if (value == '3' && getAllCRFVisitsData) {
      const temp = getAllCRFVisitsData?.map((item: any) => ({
        label:
          item.unscheduled_visit_name !== null
            ? item.unscheduled_visit_name
            : item.protocol_visit_name[0].toUpperCase() + item.protocol_visit_name.slice(1),
        key: item.unscheduled_visit_name !== null ? item.unscheduled_visit_name : item.protocol_visit_name,
        id: item.visit_id
      }));
      temp && setVisitList([{ key: 'All', label: 'All' }, ...temp]);
    }
  }, [getAllCRFVisitsIsSuccess, getAllCRFVisitsIsLoading, value]);

  useEffect(() => {
    if (getAllCRFVisitsIsError || getAllPatientDetailsIsError) {
      const errorData: any = getAllCRFVisitsError || getAllPatientDetailsError;
      if (errorData?.status == 500 || errorData?.status == 502 || errorData?.status == 503) {
        dispatch(
          setMessage({
            msgDetails: `${errorData?.status} : Internal Server Error`,
            severity: 'error'
          })
        );
      } else if (errorData?.status == 403) {
        dispatch(
          setMessage({
            msgDetails: `${errorData?.status} : Forbbiden Access`,
            severity: 'error'
          })
        );
      }
    }
  }, [getAllCRFVisitsIsError, getAllCRFVisitsError, getAllPatientDetailsError, getAllPatientDetailsIsError]);

  const {
    isCRFSummaryLoading,
    crfSummaryData,
    crfSummaryError,
    crfSummaryGetAllData,
    crfSummaryGetAllError,
    crfSummaryGetAllIsError
  } = useSubjectTracking();

  useEffect(() => {
    if (getAllPatientDetails) {
      if (selectedSubjectID) {
        const temp: any = getAllPatientDetails.selected_data.find(
          (item: any) => item?.patient?.subject_id == selectedSubjectID
        );
        setPatientID(temp?.patient?.patient_identifier);
        setSummaryData(temp.patient);
      } else {
        const temp2: any = getAllPatientDetails.selected_data.find(
          (item: any) => item?.patient?.prescreening_id == prescreen_id
        );
        setSummaryData(temp2?.patient);
      }
    }
  }, [prescreen_id, selectedSubjectID, getAllPatientDetailsIsSuccess]);

  const setUpdatedCRFData = (crfData: any) => {
    setUpdateCRFGridData(false);
  };
  useEffect(() => {
    if (updateCRFGridData) {
      let crfData = null;
      if (selectedVisitType !== 'All') {
        crfData = crfSummaryData;
      } else {
        if (crfSummaryGetAllIsError) {
          crfData = [];
        } else {
          crfData = crfSummaryGetAllData;
        }
      }
      setUpdatedCRFData(crfData);
    }
  }, [crfSummaryData, crfSummaryGetAllData, updateCRFGridData]);
  useEffect(() => {
    if (crfSummaryError || crfSummaryGetAllError) {
      setUpdateCRFGridData(false);
    }
  }, [crfSummaryError, crfSummaryGetAllError]);
  useEffect(() => {
    if ((isCRFSummaryLoading || getAllPatientDetailsIsLoading) && !isLoaderInitiated) {
      dispatch(showLoader());
    } else {
      dispatch(hideLoader());
    }
  }, [isCRFSummaryLoading, getAllPatientDetailsIsLoading]);


  const fromNavigationPathName: any = useAppSelector((state: any) => {
    return state.navigationPath.navigationPathName;
  });
  const { setTabVal, tabsData } = useTab();

  const handleBack = () => {
    if (fromNavigationPathName !== '' && fromNavigationPathName !== null) {
      const pathFromTo = fromNavigationPathName.split(':');
      if (pathFromTo[0] === 'dataReview') {
        const currentTabinfo: any = tabsData.filter((item: any) => item.key === 'dataReview');
        setTabVal(currentTabinfo[0].index);
      }
    } else {
      setShowSubjectTraking(true);
      setShowSubjectDetails(false);
    }
    dispatch(setSelectedCRFOID(''));
    dispatch(setSelectedSubject(''));
  };

  useEffect(() => {
    if (fromNavigationPathName !== '' && fromNavigationPathName !== null) {
      const pathFromTo = fromNavigationPathName.split(':');
      if (pathFromTo[0] === 'dataReview') {
        if (selectedCRF === '') {
          setValue('1');
        } else {
          setValue('3');
        }
      }
    }
  }, [fromNavigationPathName]);

  const handleSubjectIDChange = (_: any, value: any) => {
    dispatch(clearSubjectInfo());
    handleSubjectIDChangeCallBack(value);
    setSelectedSubjectID(value);
  };

  const handleVisitChange = (_: any, value: any) => {
    dispatch(clearSubjectInfo());
    setSelectedVisit(value);
  };

  const [demographics, setDemograpicData] = useState<any>([]);
  useEffect(() => {
    if (Object.keys(summaryData).length > 0) {
      setDemograpicData([
        {
          headding: 'DOB',
          type: 'date',
          data: summaryData?.date_of_birth,
          md: 6
        },
        {
          headding: 'Cancer Type',
          type: '',
          data: summaryData?.cancer_type,
          md: 6
        },
        {
          headding: 'Gender',
          type: '',
          data: summaryData?.gender,
          md: 6
        },
        {
          headding: 'Trial',
          type: '',
          data: summaryData?.trial,
          md: 6
        },
        {
          headding: 'Race',
          type: '',
          data: summaryData?.race,
          md: 12
        },
        {
          headding: 'Ethnicity',
          type: '',
          data: summaryData?.ethnicity,
          md: 12
        }
      ]);
    }
  }, [summaryData]);

  const [screenDetailsData, setscreenDetailsData] = useState<any>({
    Info: [],
    'Prescreen Details': [],
    'Screening Details': [],
    'Randomization Details': [],
    'Discontinuation Details': [],
    'Issue Tracking': []
  });
  useEffect(() => {
    setscreenDetailsData({
      ...screenDetailsData,
      Info: [
        {
          headding: 'Sl No',
          type: '',
          data: preScreenGridRowsData?.sl_no,
          md: 6
        },
        {
          headding: 'Subject Status',
          type: '',
          data: preScreenGridRowsData?.subject_status,
          md: 6
        },
        {
          headding: 'Prescreen ID',
          type: '',
          data: preScreenGridRowsData?.pre_screen_id,
          md: 6
        },
        {
          headding: 'Comment',
          type: '',
          data: preScreenGridRowsData?.comments_notes,
          md: 6
        }
      ],
      'Prescreen Details': [
        {
          headding: 'Date of Last Assessment',
          type: 'date',
          data: preScreenGridRowsData?.date_of_last_assessment,
          md: 6
        },
        {
          headding: 'Eligibility Criteria Numbers Met',
          type: '',
          data: preScreenGridRowsData?.eligibility_criteria_numbers_met_pre_screen,
          md: 6
        },
        {
          headding: 'Eligibility Criteria Numbers Not Met',
          type: '',
          data: preScreenGridRowsData?.eligibility_criteria_numbers_not_met,
          md: 6
        }
      ],
      'Screening Details': [
        {
          headding: 'Subject ID',
          type: '',
          data: preScreenGridRowsData?.subject_id,
          md: 6
        },
        {
          headding: 'Date of Consent',
          type: '',
          data: preScreenGridRowsData?.date_of_consent,
          md: 6
        },
        {
          headding: 'Screening Date',
          type: '',
          data: preScreenGridRowsData?.screening_date,
          md: 6
        }
      ],
      'Randomization Details': [
        {
          headding: 'Randomized?',
          type: '',
          data: preScreenGridRowsData?.randomized_yesno,
          md: 6
        },
        {
          headding: 'Randomization Number',
          type: '',
          data: preScreenGridRowsData?.randomization_number,
          md: 6
        },
        {
          headding: 'Eligibility Criteria Numbers Met',
          type: '',
          data: preScreenGridRowsData?.eligibility_criteria_numbers_met,
          md: 6
        }
      ],
      'Discontinuation Details': [
        {
          headding: 'IP Discounted',
          type: '',
          data: preScreenGridRowsData?.ip_discontinued,
          md: 6
        },
        {
          headding: 'Study Discontinued',
          type: '',
          data: preScreenGridRowsData?.study_discontinued,
          md: 6
        }
      ],
      'Issue Tracking': [
        {
          headding: 'Data Integration Issues',
          type: '',
          data: preScreenGridRowsData?.data_integration_issues,
          md: 6
        },
        {
          headding: 'SDV Pending',
          type: '',
          data: preScreenGridRowsData?.sdv_pending,
          md: 6
        },
        {
          headding: 'RBQM Issues',
          type: '',
          data: preScreenGridRowsData?.rbqm_issues,
          md: 6
        },
        {
          headding: 'Queries Pending',
          type: '',
          data: preScreenGridRowsData?.queries_pending,
          md: 6
        },
        {
          headding: 'Overall Subject Data Status',
          type: '',
          data: preScreenGridRowsData?.overall_subject_data_status,
          md: 6
        },
        {
          headding: 'Other Issues',
          type: '',
          data: 0,
          md: 6
        },
        {
          headding: 'Visit Overdue',
          type: '',
          data: preScreenGridRowsData?.visit_overdue,
          md: 6
        }
      ]
    });
  }, [preScreenGridRowsData]);

  return (
    <>
      <Box className="mainBackBtnWrap">
        <Button startIcon={<ArrowBackIcon fontSize="small" />} onClick={handleBack} style={{ marginBottom: '5px' }}>
          Back
        </Button>
      </Box>
      <Grid container spacing={2} mt={0}>
        <Grid item md={4}>
          <FormControl variant="standard" sx={{ minWidth: 120 }}>
            <Autocomplete
              id="subject-id-select"
              options={allSubjectsIDsList}
              defaultValue={selectedSubjectID}
              onChange={handleSubjectIDChange}
              disableClearable
              renderInput={(params: any) => <TextField {...params} label="Subject ID" />}
              sx={{
                '& .MuiOutlinedInput-root': { padding: '0 !important', paddingRight: '39px !important' },
                '& .MuiInputLabel-root': { marginLeft: '-14px' }
              }}
            />
          </FormControl>
        </Grid>
        <Grid item md={4}>
          <FormControl variant="standard" sx={{ minWidth: 120, textTransform: 'capitalize' }}>
            <Autocomplete
              id="visit-select"
              options={visitList}
              defaultValue={'All'}
              onChange={handleVisitChange}
              value={selectedVisit?.key}
              disableClearable
              renderInput={(params: any) => <TextField {...params} label="Visit" />}
              sx={{
                '& .MuiOutlinedInput-root': { padding: '0 !important', paddingRight: '39px !important' },
                '& .MuiInputLabel-root[data-shrink="true"]': { marginLeft: '-14px' }
              }}
            />
          </FormControl>
        </Grid>
      </Grid>
      <Box sx={{ width: '100%', mt: 2 }}>
        <TabContext value={value}>
          <TabList onChange={handleChangeTabs} aria-label="Study Conduct Tabs" className="tabsFontSize">
            <Tab label="Subject Summary" value="1" />
            <Tab label="Screen Details" value="2" />
            <Tab label="CRF Summary Table" value="3" />
          </TabList>
          <TabPanel value="1" sx={{ p: 0 }}>
            <Box className="borderBox" sx={{ py: 2.5, px: 2 }}>
              <Typography variant="h3" sx={{ fontSize: '1.4rem', fontWeight: 700 }} mb={1.5} mt={0}>
                Demographics
              </Typography>
              <Grid container rowSpacing={1} columnSpacing={{ md: 3 }} sx={{ typography: 'body1' }}>
                {demographics.length > 0 &&
                  demographics.map((item: any, index: any) => {
                    return (
                      <Grid key={index} item md={item.md}>
                        <Typography sx={{ fontSize: '1.125rem' }}>
                          <strong>{item.headding}: </strong>
                          <span className="results">
                            {' '}
                            {item.type == 'date'
                              ? moment(item.data, `MM/YYYY`).format(
                                  userPreferedDateFormat.replace(`dd-`, ``).replace(`dd/`, ``)
                                )
                              : item.data}
                          </span>
                        </Typography>
                      </Grid>
                    );
                  })}
              </Grid>
            </Box>
          </TabPanel>
          <TabPanel value="2" sx={{ p: 0 }}>
            {Object.keys(screenDetailsData).map((key: any, index: any) => {
              return (
                <Box key={index} className="borderBox" sx={{ py: 2.5, px: 2, mb: 2 }}>
                  <Typography variant="h3" sx={{ fontSize: '1.4rem', fontWeight: 700 }} mb={1.5} mt={0}>
                    {key}
                  </Typography>
                  <Grid container rowSpacing={1} columnSpacing={{ md: 3 }} sx={{ typography: 'body1' }}>
                    {screenDetailsData[key].length > 0 &&
                      screenDetailsData[key].map((item: any, itemIndex: any) => {
                        return (
                          <Grid key={itemIndex} item md={item.md}>
                            <Typography sx={{ fontSize: '1.125rem' }}>
                              <strong>{item.headding}: </strong>
                              <span className="results">
                                {' '}
                                {item.type == 'date' ? preferedDateFormat(item.data) : item.data}
                              </span>
                            </Typography>
                          </Grid>
                        );
                      })}
                  </Grid>
                </Box>
              );
            })}
          </TabPanel>
          <TabPanel value="3" sx={{ p: 0 }}>
            <CRFSTab
              parent_id={patientID}
              filteredVisit={selectedVisit}
              subject_id={selectedSubjectID}
              selectedCRF={selectedCRF}
            />
          </TabPanel>
        </TabContext>
      </Box>
    </>
  );
};

export default SubjectTrackingDetails;
